<template>
  <div v-if="isVisible" class="oc-import-widget" :class="{ 'oc-import-widget--disabled': !hasAction }">
    <div class="oc-import-widget__content">
      <div class="oc-import-widget__title">
        {{ $t('import_status') }}
      </div>
      <div class="oc-import-widget__subtitle">
        {{ statusText }}
      </div>
    </div>

    <!-- Action badges: appear only when something actionable happens -->
    <button
      v-if="showSuccessBadge"
      type="button"
      class="oc-import-widget__badge oc-import-widget__badge--success"
      :title="$t('batch_imported_click_to_view')"
      @click.stop="onSuccessBadgeClick"
    >
      1
    </button>

    <button
      v-if="showFailBadge"
      type="button"
      class="oc-import-widget__badge oc-import-widget__badge--fail"
      :title="$t('batch_failed_click_to_view_errors')"
      @click.stop="onFailBadgeClick"
    >
      !
    </button>

    <button
      v-if="canClose"
      type="button"
      class="oc-import-widget__close"
      :title="$t('close')"
      @click.stop="closeWidget"
    >
      ×
    </button>
  </div>
</template>

<script>
import ImportExportAPIService from '@/js/api/import-export/import-export';

export default {
  name: 'ImportStatusWidget',
  data() {
    return {
      // NOTE: ImportExportAPIService.postReviewStatus() uses session/csrf cookies in this app.
      // We do not require an access_token to poll meta; some sessions don't store it in localStorage.
      access_token: null,
      pollTimer: null,
      metaStatus: null, // queued|processing|failed|finished|null
      completedBatches: 0,
      totalBatches: 0,
      runId: null,
      ackCompletedBatches: 0,
      ackFailedRunId: null,
      // Used to make localStorage-based visibility reactive in the same tab
      lsTick: 0,
    };
  },
  computed: {
    brandSubdomain() {
      try {
        // Prefer the key written by import-export (ensures consistent localStorage keys even if store isn't ready yet)
        const storedKey = (typeof localStorage !== 'undefined') ? localStorage.getItem('oc_products_import_widget_brand') : null;
        if (storedKey) return storedKey;
        return (this.$store && this.$store.getters && this.$store.getters.brand && this.$store.getters.brand['subdomain'])
          ? this.$store.getters.brand['subdomain']
          : 'default';
      } catch (e) {
        return 'default';
      }
    },
    inProgressKey() {
      return `oc_products_import_in_progress_${this.brandSubdomain}`;
    },
    widgetActiveKey() {
      // Widget must persist across pages even after import finishes until user closes it
      return `oc_products_import_widget_active_${this.brandSubdomain}`;
    },
    ackCompletedKey() {
      // Stores "{runId}:{ackCompletedBatches}" so success badge stays cleared after reload.
      return `oc_products_import_widget_ack_completed_${this.brandSubdomain}`;
    },
    ackFailedKey() {
      // Stores "{runId}" so failed badge stays cleared after reload.
      return `oc_products_import_widget_ack_failed_${this.brandSubdomain}`;
    },
    isVisible() {
      // eslint-disable-next-line no-unused-expressions
      this.lsTick;
      try {
        const ls = window.localStorage;
        return (ls.getItem(this.widgetActiveKey) === '1') || (ls.getItem(this.inProgressKey) === '1');
      } catch (e) {
        return false;
      }
    },
    statusText() {
      if (this.metaStatus === 'finished') return 'Import completed. Click to view.';
      if (this.metaStatus === 'failed') return 'Import failed. Click to view errors.';
      if (this.metaStatus === 'queued') return 'Import queued…';
      if (this.metaStatus === 'processing') return 'Import in progress…';
      return 'Import in progress…';
    },
    canClose() {
      // Requirement: show close button when import completes (and also allow close on failure)
      if (this.metaStatus === 'finished' || this.metaStatus === 'failed') return true;
      // Safety: if backend status didn't flip but we completed all batches, treat as finished.
      return (this.totalBatches > 0 && this.completedBatches >= this.totalBatches);
    },
    showSuccessBadge() {
      // Requirement: show green "1" only when a batch completes, and hide it after click.
      // We display "1" for any unacknowledged completion (per run).
      return (this.completedBatches > this.ackCompletedBatches) && this.metaStatus !== 'failed';
    },
    showFailBadge() {
      // Requirement: show yellow "!" only when a batch fails, and hide it after click.
      return this.metaStatus === 'failed' && !!this.runId && this.ackFailedRunId !== this.runId;
    },
    hasAction() {
      // Requirement: hover/click not available until we have either success or failure badge.
      return this.showSuccessBadge || this.showFailBadge;
    },
  },
  created() {
    // Restore ack state so badges stay dismissed across reloads.
    try {
      const ls = window.localStorage;

      const ack = ls.getItem(this.ackCompletedKey);
      if (ack && typeof ack === 'string' && ack.includes(':')) {
        const parts = ack.split(':');
        const run = parts[0] || null;
        const cnt = parseInt(parts[1] || '0', 10) || 0;
        this.runId = run;
        this.ackCompletedBatches = cnt;
      }

      const ackFail = ls.getItem(this.ackFailedKey);
      if (ackFail) this.ackFailedRunId = String(ackFail);
    } catch (e) {}
  },
  watch: {
    isVisible: {
      immediate: true,
      handler(val) {
        if (val) this.startPolling();
        else this.stopPolling();
      },
    },
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    startPolling() {
      if (this.pollTimer) return;
      // Poll meta only while widget is visible (reduces API calls)
      this.pollOnce();
      this.pollTimer = setInterval(() => this.pollOnce(), 15000);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    pollOnce() {
      let formData = new FormData();
      formData.append('type', 'large');
      formData.append('meta_only', 1);
      formData.append('_ts', Date.now());

      ImportExportAPIService.postReviewStatus(formData).then((response) => {
        const meta = response.data && response.data.meta ? response.data.meta : null;
        this.metaStatus = meta && meta.status ? meta.status : null;
        this.completedBatches = meta && meta.completed_batches ? parseInt(meta.completed_batches, 10) : 0;
        this.totalBatches = meta && meta.total_batches ? parseInt(meta.total_batches, 10) : 0;
        this.runId = meta && meta.run_id ? String(meta.run_id) : this.runId;

        // If all batches are completed but status wasn't flipped, treat as finished for UI controls.
        if (this.metaStatus !== 'failed' && this.totalBatches > 0 && this.completedBatches >= this.totalBatches) {
          this.metaStatus = 'finished';
        }

        // If run_id changed, reset ack completed state so new import can show badges.
        try {
          const ls = window.localStorage;
          const ack = ls.getItem(this.ackCompletedKey);
          const ackRun = (ack && ack.includes(':')) ? ack.split(':')[0] : null;
          if (this.runId && ackRun && ackRun !== this.runId) {
            this.ackCompletedBatches = 0;
            ls.setItem(this.ackCompletedKey, `${this.runId}:0`);
          }
        } catch (e) {}

        // Safety:
        // If there is no large-import meta and we don't have the "in progress" flag,
        // then this widget should not be visible (prevents showing for <800 imports).
        if (!meta) {
          try {
            const ls = window.localStorage;
            const inProgress = ls.getItem(this.inProgressKey) === '1';
            if (!inProgress) {
              ls.removeItem(this.widgetActiveKey);
            }
          } catch (e) {}
        }

        // Touch tick so visibility updates if localStorage changed
        this.lsTick = Date.now();
      }).catch(() => {});
    },
    openStatus() {
      try {
        this.$router.push({ path: '/dashboard/products/import-status', query: { type: 'large' } });
      } catch (e) {
        window.location.href = '/dashboard/products/import-status?type=large';
      }
    },
    onSuccessBadgeClick() {
      // Open status page and dismiss the green badge for the currently completed batches.
      this.openStatus();
      try {
        const ls = window.localStorage;
        const run = this.runId || 'unknown';
        this.ackCompletedBatches = this.completedBatches;
        ls.setItem(this.ackCompletedKey, `${run}:${this.ackCompletedBatches}`);
      } catch (e) {}
    },
    onFailBadgeClick() {
      // Open status page and dismiss the yellow badge for this failed run.
      this.openStatus();
      try {
        const ls = window.localStorage;
        if (this.runId) {
          this.ackFailedRunId = this.runId;
          ls.setItem(this.ackFailedKey, this.runId);
        }
      } catch (e) {}
    },
    closeWidget() {
      // Requirement: widget stays until import completes/fails; then user can close it
      try {
        const ls = window.localStorage;
        ls.removeItem(this.widgetActiveKey);
        ls.removeItem(this.inProgressKey);
        ls.removeItem(this.ackCompletedKey);
        ls.removeItem(this.ackFailedKey);
        ls.removeItem('oc_products_import_widget_brand');
      } catch (e) {}
      this.metaStatus = null;
      this.completedBatches = 0;
      this.totalBatches = 0;
      this.runId = null;
      this.ackCompletedBatches = 0;
      this.ackFailedRunId = null;
      this.lsTick = Date.now();
      this.stopPolling();

      // Destroy/unmount the widget completely (it will be mounted again on next large import start).
      try {
        if (typeof window !== 'undefined' && typeof window.ocUnmountProductsImportWidget === 'function') {
          window.ocUnmountProductsImportWidget();
        }
      } catch (e) {}
    },
  },
};
</script>

<style scoped>
.oc-import-widget {
  position: fixed;
  right: 18px;
  bottom: 18px;
  z-index: 9999;
  background: #ffffff;
  border: 1px solid rgba(0,0,0,0.10);
  box-shadow: 0 8px 24px rgba(0,0,0,0.18);
  border-radius: 12px;
  padding: 10px 12px;
  min-width: 220px;
  cursor: default;
  display: flex;
  align-items: center;
  gap: 10px;
}

.oc-import-widget--disabled {
  opacity: 0.65;
}

.oc-import-widget__content {
  flex: 1;
}

.oc-import-widget__badge {
  border: 0;
  width: 24px;
  height: 24px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 14px;
  line-height: 22px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.oc-import-widget__badge--success {
  background: #16a34a; /* green */
  color: #ffffff;
}

.oc-import-widget__badge--fail {
  background: #f59e0b; /* yellow */
  color: #111827;
}

.oc-import-widget__title {
  font-weight: 700;
  color: #111827;
  font-size: 14px;
  line-height: 18px;
}

.oc-import-widget__subtitle {
  color: #6b7280;
  font-size: 12px;
  line-height: 16px;
  margin-top: 2px;
}

.oc-import-widget__close {
  position: absolute;
  top: -8px;
  right: -8px;
  border: 0;
  background: #dc2626; /* red */
  color: #ffffff;
  width: 24px;
  height: 24px;
  border-radius: 999px;
  line-height: 20px;
  font-size: 18px;
  cursor: pointer;
}
</style>
